import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../../../../../store/utility';
import { backend_server_address } from '../../../../../backendServer';
import type { OfflineVariableRow } from '../types';

// Offline analysis requests for a machine, backed by the real endpoint
// /api/equipment_nodes/offline_analysis_request/ (OfflineAnalysisRequestViewSet).
// `requested_by` / `closed_by` are bare user ids (resolved to names client-side).
export interface OfflineAnalysisRequest {
    id: number;
    node: number;
    technique: string;
    status: 'open' | 'closed';
    requested_at: string;
    requested_by: number | null;
    closed_at: string | null;
    closed_by: number | null;
}

interface ListParams {
    node: number;
    status?: 'open' | 'closed';
    technique?: string;
}

// Per-technique offline status for a machine, backed by
// /api/equipment_nodes/offline_status/?node=<id> (OfflineTechniqueStatusViewSet).
// One entry per enabled offline technique. `last_report` is null when there is no
// report or the caller lacks `reports.view_report`.
export interface OfflineTechniqueStatus {
    technique: string;
    last_measurement_date: string | null;
    last_report: { report_id: number; date: string; download_url: string | null } | null;
    interval_days: number;
    is_due: boolean;
    /**
     * Whether the technique CAN report per-variable readings. False for ultrasound and
     * electrical analysis, which have no measurement model — that is what tells a
     * header-only card apart from a route with no samples yet.
     */
    variables_supported: boolean;
    /** Populated only when the query asks for `include_variables`. */
    variables: OfflineVariableRow[];
}

export const offlineAnalysisAPI = createApi({
    reducerPath: 'offlineAnalysisApi',
    baseQuery: axiosBaseQuery({ baseUrl: backend_server_address + '/api/equipment_nodes/' }),
    tagTypes: ['OfflineAnalysisRequest'],
    endpoints: (builder) => ({
        getOfflineStatus: builder.query<OfflineTechniqueStatus[], { node: number; includeVariables?: boolean }>({
            query: ({ node, includeVariables }) => ({
                url: 'offline_status/',
                method: 'GET',
                withCredentials: true,
                // Reading the variables walks every offline measurement point, so it is
                // opt-in: the drawer asks for them, other callers do not.
                params: { node, ...(includeVariables ? { include_variables: 'true' } : {}) },
            }),
        }),
        getOfflineAnalysisRequests: builder.query<OfflineAnalysisRequest[], ListParams>({
            query: ({ node, status, technique }) => ({
                url: 'offline_analysis_request/',
                method: 'GET',
                withCredentials: true,
                params: { node, ...(status ? { status } : {}), ...(technique ? { technique } : {}) },
            }),
            providesTags: (_r, _e, arg) => [{ type: 'OfflineAnalysisRequest', id: arg.node }],
        }),
        createOfflineAnalysisRequest: builder.mutation<OfflineAnalysisRequest, { node: number; technique: string }>({
            query: (data) => ({
                url: 'offline_analysis_request/',
                method: 'POST',
                withCredentials: true,
                data,
            }),
            // The POST returns the created request (201), so patch it straight into the
            // open-requests cache instead of refetching the list.
            async onQueryStarted({ node }, { dispatch, queryFulfilled }) {
                try {
                    const { data: created } = await queryFulfilled;
                    dispatch(
                        offlineAnalysisAPI.util.updateQueryData(
                            'getOfflineAnalysisRequests',
                            { node, status: 'open' },
                            (draft) => {
                                const idx = draft.findIndex((r) => r.technique === created.technique);
                                if (idx >= 0) draft[idx] = created;
                                else draft.push(created);
                            },
                        ),
                    );
                } catch {
                    // Request failed; the component surfaces the error and the cache is unchanged.
                }
            },
        }),
    }),
});

export const { useGetOfflineStatusQuery, useGetOfflineAnalysisRequestsQuery, useCreateOfflineAnalysisRequestMutation } =
    offlineAnalysisAPI;
export const offlineAnalysisAPIReducer = offlineAnalysisAPI.reducer;
export const offlineAnalysisAPIReducerPath = offlineAnalysisAPI.reducerPath;
export const offlineAnalysisAPIReducerMiddleware = offlineAnalysisAPI.middleware;
